import { z } from "zod";
import { bookingForSchema, classFillSchema, type BookingFor, type ClassFill, type SetupStepKey, type Sport } from "./enums.ts";
import type { BusinessLabels } from "./schemas/business.ts";

/**
 * The business shape (migration 0069, strategy repo `teamna/prototypes/pilates-studio-plan.html`,
 * feature F1): who books (`bookingFor`) and how classes fill (`classFill`). Screens read the shape
 * through `useShape()`, never the sport. The sport only sets the start values (`defaultShape`), the
 * same way it sets the start words (`defaultLabels`).
 */
export const businessShapeSchema = z.object({ bookingFor: bookingForSchema, classFill: classFillSchema });
export type BusinessShape = z.infer<typeof businessShapeSchema>;

/**
 * The shape a new business starts with, from its sport. Migration 0069 backfills with the same
 * table: a business that places each child in a group books for children in fixed groups; a
 * business where adults book any class books for itself and picks each class. Any other activity
 * starts as families who pick each class.
 */
export function defaultShape(sport: Sport): BusinessShape {
  switch (sport) {
    case "swimming":
    case "football":
    case "gymnastics":
      return { bookingFor: "children", classFill: "fixed_group" };
    case "pilates":
    case "padel":
      return { bookingFor: "self", classFill: "pick_class" };
    default:
      return { bookingFor: "children", classFill: "pick_class" };
  }
}

/**
 * The shape of a business as an answer carries it. A field that is missing (an older API, an old
 * test fixture) reads as the sport's start value, so a reader never has to know the defaults.
 */
export function businessShape(source: {
  bookingFor?: BookingFor | null;
  classFill?: ClassFill | null;
  sport?: Sport | null;
}): BusinessShape {
  const start = defaultShape(source.sport ?? "other");
  return { bookingFor: source.bookingFor ?? start.bookingFor, classFill: source.classFill ?? start.classFill };
}

/**
 * The owner setup steps of a business of this shape, in order (feature O1). Where clients book for
 * themselves, the owner puts nobody in a group, so there is no placement step. The API builds its
 * checklist in this order, and Desk numbers the steps from it, so "step 7 of 7" means the same on
 * Home, on the setup pages and in the mobile drawer.
 */
export function setupOrder(bookingFor: BookingFor): SetupStepKey[] {
  const steps: SetupStepKey[] = ["business", "team", "sessions", "packages", "members_imported", "invite_sent", "placement", "balances"];
  return bookingFor === "self" ? steps.filter((key) => key !== "placement") : steps;
}

/** The analytics context of a shape: the two keys every event carries (`booking_for`, `class_fill`). */
export function shapeAnalyticsProps(shape: BusinessShape): { booking_for: BookingFor; class_fill: ClassFill } {
  return { booking_for: shape.bookingFor, class_fill: shape.classFill };
}

export type ShapeQuestion<T extends string> = {
  title: string;
  options: Array<{ value: T; label: string }>;
  /** One line that tells what the chosen value does. */
  preview: (value: T) => string;
};

/**
 * The words of the two shape questions, in the business's own words. Desk asks them (Settings and
 * the sign-up) and the mobile app shows the current answers, so both read this one copy.
 */
export function shapeQuestions(labels: BusinessLabels): { bookingFor: ShapeQuestion<BookingFor>; classFill: ShapeQuestion<ClassFill> } {
  const cap = (word: string) => (word.length === 0 ? word : word[0]!.toUpperCase() + word.slice(1));
  const members = labels.memberPlural;
  const member = labels.memberSingular;
  const participant = labels.participantSingular;
  const group = labels.groupSingular;
  return {
    bookingFor: {
      title: "Who books",
      options: [
        { value: "children", label: `${cap(members)} book for their children` },
        { value: "self", label: `${cap(members)} book for themselves` },
      ],
      preview: (value) =>
        value === "children"
          ? `A ${member} adds their children and books a session for each child.`
          : `A ${member} books a place for themselves. Teamna shows no children and no families.`,
    },
    classFill: {
      title: "How classes fill",
      options: [
        { value: "fixed_group", label: `Each ${participant} stays in one ${group}` },
        { value: "pick_class", label: `${cap(members)} pick each session` },
      ],
      preview: (value) =>
        value === "fixed_group"
          ? `You put each ${participant} in a ${group}. They book the sessions of that ${group}.`
          : `${cap(members)} book any session that has a free place.`,
    },
  };
}

/** The current shape in one sentence per question, for a screen that shows it and does not edit it. */
export function shapeSummary(shape: BusinessShape, labels: BusinessLabels): { bookingFor: string; classFill: string } {
  const questions = shapeQuestions(labels);
  const labelOf = <T extends string>(question: ShapeQuestion<T>, value: T) =>
    question.options.find((option) => option.value === value)?.label ?? value;
  return {
    bookingFor: labelOf(questions.bookingFor, shape.bookingFor),
    classFill: labelOf(questions.classFill, shape.classFill),
  };
}
